import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Card, at console scale.
 *
 * This was the phone app's card: a 28px radius, a 2px tinted border, the clay
 * shadow and 24px of padding on every side. One of those behind a hero panel is
 * lovely; twenty of them stacked down an admin page turn into a bag of rounded
 * lozenges, and the diffuse clay shadows muddy into each other until nothing
 * reads as having an edge.
 *
 * It is now the same surface as `.console-panel` — hairline ring, short drop,
 * 14px radius, 14px padding — so the pages still built out of `Card` match the
 * ones rebuilt on `DetailPanel` instead of looking a generation apart. Anything
 * that genuinely wants the brand treatment (the sign-in card) can ask for it
 * with `className`.
 */
const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'rounded-panel bg-card text-card-foreground shadow-panel transition-shadow duration-fast',
        className,
      )}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      // A header is a label for what follows, so it sits on its own hairline
      // rather than floating in the same box of air as the content.
      className={cn('flex flex-col gap-0.5 border-b border-border px-3.5 py-2.5', className)}
      {...props}
    />
  ),
);
CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      // 14px bold, not 24px semibold: a panel heading is navigation, not the
      // page's headline. The h1 in the layout header is the headline.
      className={cn('font-heading text-sm font-bold leading-tight tracking-tight', className)}
      {...props}
    />
  ),
);
CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('text-micro text-muted-foreground', className)} {...props} />
  ),
);
CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    // No `pt-0`: the header now carries a border, so the content needs its own
    // top padding to sit off it.
    <div ref={ref} className={cn('p-3.5', className)} {...props} />
  ),
);
CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex items-center gap-2 border-t border-border px-3.5 py-2.5', className)}
      {...props}
    />
  ),
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
